import { useEditorState } from 'common/Prose/EditorProvider/hooks/useEditorState'
import { useEditorView } from 'common/Prose/EditorProvider/hooks/useEditorView'
import { useStateRef } from 'hooks/useStateRef'
import React, { useCallback, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { AlmanacDropCursorPluginState } from '../AlmanacDropCursor/AlmanacDropCursor'
import { HandleType, NodeHandlesPluginKey, NodeHandlesPluginState } from './NodeHandles'
import styles from './NodeHandles.module.scss'
import { observer } from 'mobx-react-lite'
import { InsertHandle } from 'common/Prose/Plugins/NodeHandles/InsertHandle/InsertHandle'
import { DragHandle } from './DragHandle/DragHandle'
import { ALMANAC_DROP_CURSOR_PLUGIN_KEY } from '../AlmanacDropCursor/constants'

export const NodeHandleManager: React.FC<React.PropsWithChildren<unknown>> = (props) => {
  const state = useEditorState()
  const [hoveredNodePos, setHoveredNodePos] = useState<number | undefined>()

  const [handleEls, setHandleEls] = useStateRef(
    Array.from(document.querySelectorAll(`.${styles.DragHandle}, .${styles.InsertHandle}`)),
  )
  useLayoutEffect(() => {
    const pluginState: NodeHandlesPluginState = NodeHandlesPluginKey.getState(state)
    if (!pluginState) return

    let modified = false
    if (pluginState.hoveredNode?.pos !== hoveredNodePos) {
      setHoveredNodePos(pluginState.hoveredNode?.pos)
      modified = true
    }
    /**
     * sometimes prosemirror will rerender the decorations using different nodes, and thus the ones we stored will be
     * "stale" and disconnected. In that case we just reacquire them.
     */
    if (handleEls.some((el) => !el.isConnected)) {
      modified = true
    }
    if (modified) {
      setHandleEls(Array.from(document.querySelectorAll(`.${styles.DragHandle}, .${styles.InsertHandle}`)))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredNodePos, setHandleEls, state])

  let handleTypes: Array<'caret' | 'hovered'> = []

  const isDragging = (ALMANAC_DROP_CURSOR_PLUGIN_KEY.getState(state) as AlmanacDropCursorPluginState | undefined)
    ?.isDragging
  if (!isDragging) {
    if (hoveredNodePos !== undefined) handleTypes.push('hovered')
  } else {
    handleTypes = ['hovered']
  }
  return (
    <>
      {handleEls.map((handleEl, idx) => {
        const handleType = handleTypes[idx]
        if (!handleType) return null
        return createPortal(
          <NodeHandle
            source={handleType}
            type={handleEl.matches(`.${styles.DragHandle}`) ? HandleType.DRAG_HANDLE : HandleType.INSERT_HANDLE}
          />,
          handleEl,
          handleType,
        )
      })}
    </>
  )
}

type NodeHandleProps = {
  source: 'caret' | 'hovered'
  type: HandleType
}

export const NodeHandle: React.FC<React.PropsWithChildren<NodeHandleProps>> = observer(({ source, type }) => {
  const editorView = useEditorView()
  const getPos = useCallback(() => {
    const dragHandlesPluginState: NodeHandlesPluginState | undefined = NodeHandlesPluginKey.getState(editorView.state)
    return dragHandlesPluginState?.hoveredNode?.pos
  }, [editorView])

  if (type === HandleType.INSERT_HANDLE) {
    return <InsertHandle getPos={getPos} />
  }

  return <DragHandle getPos={getPos} />
})
